import { useEffect, useId, useState } from "react";
import clsx from "clsx";
import {
  buildCalendarMonthGrid,
  calendarWeekdayLabels,
  canShowPreviousCalendarMonth,
  formatArrivalDayLong,
  formatCalendarMonthTitle,
  initialCalendarMonth,
  shiftCalendarMonth,
  type CalendarMonthView,
} from "@/lib/bookingDates";

export interface BookingDateCalendarProps {
  value: string;
  min?: string;
  max?: string;
  /** Festes Referenzdatum (z. B. Anreise beim Abreise-Picken), bleibt im Kalender sichtbar. */
  anchorDate?: string;
  onChange: (iso: string) => void;
  labelledBy?: string;
  className?: string;
}

export function BookingDateCalendar({
  value,
  min,
  max,
  anchorDate,
  onChange,
  labelledBy,
  className,
}: BookingDateCalendarProps) {
  const gridId = useId();
  const [month, setMonth] = useState<CalendarMonthView>(() => initialCalendarMonth(value, min));

  useEffect(() => {
    setMonth(initialCalendarMonth(value, min));
  }, [value, min]);

  const cells = buildCalendarMonthGrid(month, min, max);
  const canGoPrev = canShowPreviousCalendarMonth(month, min);

  return (
    <div
      className={clsx("eh-date-calendar", className)}
      role="application"
      aria-labelledby={labelledBy}
      onClick={(e) => e.stopPropagation()}
    >
      {anchorDate ? (
        <p className="eh-date-calendar__anchor-hint">
          Anreise: <span className="font-medium text-label">{formatArrivalDayLong(anchorDate)}</span>
        </p>
      ) : null}
      <div className="eh-date-calendar__nav">
        <button
          type="button"
          className="eh-date-calendar__nav-btn"
          aria-label="Vorheriger Monat"
          disabled={!canGoPrev}
          onClick={() => setMonth((m) => shiftCalendarMonth(m, -1))}
        >
          ‹
        </button>
        <p className="eh-date-calendar__title" aria-live="polite">
          {formatCalendarMonthTitle(month)}
        </p>
        <button
          type="button"
          className="eh-date-calendar__nav-btn"
          aria-label="Nächster Monat"
          onClick={() => setMonth((m) => shiftCalendarMonth(m, 1))}
        >
          ›
        </button>
      </div>

      <div className="eh-date-calendar__weekdays" aria-hidden="true">
        {calendarWeekdayLabels().map((label) => (
          <span key={label} className="eh-date-calendar__weekday">
            {label}
          </span>
        ))}
      </div>

      <div id={gridId} className="eh-date-calendar__grid" role="grid">
        {cells.map((cell) => {
          const selected = value === cell.iso;
          const anchored = Boolean(anchorDate && anchorDate === cell.iso && !selected);
          return (
            <button
              key={cell.iso}
              type="button"
              role="gridcell"
              aria-selected={selected}
              aria-disabled={cell.disabled}
              disabled={cell.disabled}
              className={clsx(
                "eh-date-calendar__day",
                !cell.inMonth && "eh-date-calendar__day--outside",
                cell.disabled && "eh-date-calendar__day--disabled",
                anchored && "eh-date-calendar__day--anchor",
                selected && "eh-date-calendar__day--selected",
              )}
              onClick={() => onChange(cell.iso)}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
